import { useState } from 'react';
import type { Selection } from 'react-aria-components';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { MultiSelectMenu } from '@gonfalon/ui/multi-select-with-search';
import { IconButton } from '@launchpad-ui/components';

import { attributeColumnKey } from './internal/columnKeys';
import type { DataTableAttributeColumns } from './types';
import type { ResolvedColumn } from './useDataTableColumns';

type Props<T> = {
  columns: Array<ResolvedColumn<T>>;
  hiddenColumns: Set<string>;
  attributeColumns?: DataTableAttributeColumns<T>;
  attributeColumnKeys: string[];
  onToggleVisibility: (id: string, isVisible: boolean) => void;
  onAddAttributeColumn: (key: string) => void;
  onRemoveAttributeColumn: (key: string) => void;
};

export function ColumnManager<T>({
  columns,
  hiddenColumns,
  attributeColumns,
  attributeColumnKeys,
  onToggleVisibility,
  onAddAttributeColumn,
  onRemoveAttributeColumn,
}: Props<T>) {
  const managedColumns = columns.filter((column) => column.name.trim() !== '');
  const isColumnVisible = (column: ResolvedColumn<T>) => column.isHideable === false || !hiddenColumns.has(column.id);
  const visibleColumnCount = managedColumns.filter(isColumnVisible).length;
  const selectedAttributeKeys = new Set(attributeColumnKeys);
  const availableAttributeKeys = new Set(attributeColumns?.availableKeys ?? []);
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const isSearchControlled = attributeColumns?.onSearchQueryChange !== undefined;
  const searchQuery = isSearchControlled ? (attributeColumns?.searchQuery ?? '') : localSearchQuery;
  const onSearchQueryChange = attributeColumns?.onSearchQueryChange ?? setLocalSearchQuery;
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const attributeLabel = (key: string) => attributeColumns?.getLabel?.(key) ?? key;

  const matchesQuery = (value: string) => !normalizedQuery || value.toLowerCase().includes(normalizedQuery);

  const visibleManagedColumns = managedColumns.filter(
    (column) => matchesQuery(column.name) || matchesQuery(column.ref.id),
  );
  const unselectedAttributeKeys = [...availableAttributeKeys].filter(
    (key) => !selectedAttributeKeys.has(key) && matchesQuery(attributeLabel(key)),
  );

  const items = [
    ...visibleManagedColumns.map((column) => ({
      id: column.id,
      attributeKey: column.ref.id,
      label: column.name,
      isAddableAttribute: false,
      isRemovableAttribute: column.isAttribute && availableAttributeKeys.has(column.ref.id),
    })),
    ...unselectedAttributeKeys.map((key) => ({
      id: attributeColumnKey(key),
      attributeKey: key,
      label: attributeLabel(key),
      isAddableAttribute: true,
      isRemovableAttribute: false,
    })),
  ];
  const selectedKeys = new Set(visibleManagedColumns.filter(isColumnVisible).map((column) => column.id));
  const disabledKeys = visibleManagedColumns
    .filter((column) => column.isHideable === false || (isColumnVisible(column) && visibleColumnCount === 1))
    .map((column) => column.id);

  const onSelectionChange = (keys: Selection) => {
    const nextKeys = keys === 'all' ? new Set(items.map((item) => item.id)) : new Set([...keys].map(String));
    for (const item of items) {
      const isSelected = nextKeys.has(item.id);
      if (isSelected === selectedKeys.has(item.id)) {
        continue;
      }
      if (item.isAddableAttribute) {
        onAddAttributeColumn(item.attributeKey);
      } else if (item.isRemovableAttribute && !isSelected) {
        onRemoveAttributeColumn(item.attributeKey);
      } else {
        onToggleVisibility(item.id, isSelected);
      }
    }
  };

  return (
    <MultiSelectMenu
      trigger={
        <IconButton
          aria-label={msg('ui.dataTable.columnManager.triggerLabel')}
          icon="more-horiz"
          size="small"
          variant="minimal"
        />
      }
      placement="bottom end"
      aria-label={msg('ui.dataTable.columnManager.dialogLabel')}
      header={<Message id="ui.dataTable.columnManager.heading" />}
      items={items.map((item) => ({ id: item.id, label: item.label }))}
      selectedKeys={selectedKeys}
      disabledKeys={disabledKeys}
      onSelectionChange={onSelectionChange}
      escapeKeyBehavior="none"
      search={{
        'aria-label': msg('ui.dataTable.columnManager.searchLabel'),
        placeholder: msg('ui.dataTable.columnManager.searchPlaceholder'),
        value: searchQuery,
        onChange: onSearchQueryChange,
      }}
      emptyState={<Message id="ui.dataTable.columnManager.noResults" />}
      isLoading={attributeColumns?.isLoading}
      loadingLabel={msg('ui.dataTable.columnManager.loadingLabel')}
    />
  );
}
